/* eslint-disable spire/export-styles */
import CarotekColors from "@carotek/utils/Colors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state),
    addressPageState: getPageLinkByPageType(state, "CheckoutShippingPage"),
    orderSummaryPageState: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
    orderConfirmationPageState: getPageLinkByPageType(state, "OrderConfirmationPage"),
});

export interface CheckoutProgressType {
    container?: InjectableCss;
    titleContainer?: InjectableCss;
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    pagesLinkContainer?: InjectableCss;
    indicator?: InjectableCss;
    pageTitle?: TypographyPresentationProps;
    currentIndicator?: InjectableCss;
    currentPageTitle?: TypographyPresentationProps;
    prevIndicator?: InjectableCss;
    prevtPageTitle?: LinkPresentationProps;
    prevtPageStatickTitle?: TypographyPresentationProps;
}

const checkoutProgressStyles: CheckoutProgressType = {
    container: {
        css: css`
            padding: 10px 0 5px;
            display: flex;
            align-items: center;
            border-bottom: 1px solid ${CarotekColors.grey1};
            margin-bottom: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-bottom: 20px !important;
                        `,
                        css`
                            flex-wrap: wrap;
                            gap: 20px;
                            padding-bottom: 60px;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    title: {
        color: "text.main",
        weight: 700,
        size: "36px",
        lineHeight: "45px",
        fontFamily: "OpenSansBold",
        css: css`
            margin-right: 1em;
            margin-bottom: 0;
            margin-top: 0;
        `,
    },
    titleContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            transform: translateY(5px);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-wrap: wrap;
                            gap: 15px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    pagesLinkContainer: {
        css: css`
            padding-right: 50px;
            display: flex;
            align-items: flex-end;
            position: relative;
            ::after {
                content: ">";
                position: absolute;
                right: 20px;
            }

            :last-child {
                ::after {
                    display: none;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 0;
                        `,
                        css`
                            margin-bottom: 1rem;
                        `,
                        css`
                            margin-bottom: 1rem;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    indicator: {
        css: css`
            width: 27px;
            height: 27px;
            border-radius: 50%;
            margin-right: 5px;
        `,
    },
    pageTitle: {
        color: "text.main",
        size: 16,
        lineHeight: "1.688rem",
        weight: 400,
    },
    currentPageTitle: {
        size: 16,
        lineHeight: "1.688rem",
        fontFamily: "OpenSansBold",
        weight: 700,
        css: css``,
    },
    currentIndicator: {
        css: css`
            width: 27px;
            height: 27px;
            border-radius: 50%;
            margin-right: 5px;
            display: flex;
            align-items: center;
            justify-content: center;

            svg {
                transform: scale(1.4);
            }
        `,
    },
    prevIndicator: {
        css: css`
            width: 27px;
            height: 27px;
            border-radius: 50%;
            margin-right: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
            display: flex;
            align-items: center;
            justify-content: center;

            svg {
                transform: scale(1.3);
            }
        `,
    },
    prevtPageTitle: {
        css: css`
            span {
                font-size: 14px;
                line-height: 1.688rem;

                :hover {
                    text-decoration: underline;
                }
            }
        `,
    },
    prevtPageStatickTitle: {
        size: 16,
        lineHeight: "1.688rem",
        fontFamily: "OpenSansBold",
        css: css`
            span {
            }
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = checkoutProgressStyles;

interface checkoutPageState {
    url: string;
    title: string;
    index: number;
    mainTitle: string;
}

const CheckoutProgressBar: React.FunctionComponent<Props> = ({
    currentPage,
    addressPageState,
    orderConfirmationPageState,
    orderSummaryPageState,
}: Props) => {
    const [checkoutPages, setCheckoutPages] = React.useState<checkoutPageState[] | null>(null);
    const [currentCheckoutPageIndex, setCurrentCheckoutPageIndex] = React.useState<number>(0);

    React.useEffect(() => {
        const initialState: checkoutPageState[] = [];

        if (addressPageState) {
            initialState.push({
                index: 0,
                title: "Billing & Shipping",
                url: addressPageState.url,
                mainTitle: addressPageState.title,
            });
        }

        if (orderSummaryPageState) {
            initialState.push({
                index: 2,
                title: "Payment",
                url: orderSummaryPageState.url,
                mainTitle: orderSummaryPageState.title,
            });
        }
        if (orderConfirmationPageState) {
            initialState.push({
                index: 3,
                title: "Confirmation",
                url: orderConfirmationPageState.url,
                mainTitle: orderConfirmationPageState.title,
            });
        }
        setCheckoutPages(initialState);
    }, [addressPageState, orderConfirmationPageState, orderSummaryPageState]);

    React.useEffect(() => {
        const currentState = checkoutPages?.find(page => page.mainTitle === currentPage.fields.title);

        if (currentState) {
            setCurrentCheckoutPageIndex(currentState.index);
        }
    }, [checkoutPages, currentPage]);

    if (!checkoutPages || currentCheckoutPageIndex === undefined) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.titleContainer}>
                <Typography {...styles.title} as="h1" variant="h1">
                    {translate("Checkout")}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                {checkoutPages.map(page => {
                    const isCurrentPageLink = page.index === currentCheckoutPageIndex;
                    const prevPages = page.index < currentCheckoutPageIndex;
                    const pageTitle = page.title.includes("Checkout - ")
                        ? page.title.replace("Checkout - ", "")
                        : page.title;
                    return (
                        <StyledWrapper key={page.index} {...styles.pagesLinkContainer}>
                            {prevPages ? (
                                <>
                                    {currentPage.type !== orderConfirmationPageState?.type ? (
                                        <Link {...styles.prevtPageTitle} href={page.url}>
                                            {pageTitle}
                                        </Link>
                                    ) : (
                                        <Typography {...styles.prevtPageStatickTitle}>{pageTitle}</Typography>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Typography {...(isCurrentPageLink ? styles.currentPageTitle : styles.pageTitle)}>
                                        {pageTitle}
                                    </Typography>
                                </>
                            )}
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutProgressBar),
    definition: {
        group: "Common",
        icon: "heading",
        allowedContexts: [
            "CartPage",
            "CheckoutShippingPage",
            "CheckoutReviewAndSubmitPage",
            "OnePageCheckoutPage",
            "OrderConfirmationPage",
        ],
    },
};

export default widgetModule;
